import { ArrowRightIcon } from 'assets/icons';
import { TaxesAndFeesResponse } from 'containers/invoices/invoices.model';
import {
  InvoiceLevelPaymentModes,
  selectAvailablePaymentModes,
  selectInvoiceFinalAmount,
  selectInvoicePaymentModes,
  selectNoPaymentMethodsAvailable,
  selectTipEnabled,
  toggleTipEnabled,
  updateInvoicePaymentModes,
  updateTipEnabled
} from 'containers/invoices/InvoicesSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, useContext, useEffect } from 'react';
import { InvoiceTabEnum } from '../constants';
import { CustomSwitch } from 'components';
import { openScanpayFAQInNewTab } from 'utils/urlUtils';
import { CleverTapEventsInvoices } from 'containers/invoices/events';
import InvoicePaymentMethodsSection from './InvoicePaymentMethodsSection';

interface PaymentOptionsSectionProps {
  isInvoiceEdit?: boolean;
  className?: string;
  taxesAndFees?: TaxesAndFeesResponse;
  context: React.Context<any>;
}

const PaymentOptionsSection: FC<PaymentOptionsSectionProps> = ({
  taxesAndFees = null,
  context,
  isInvoiceEdit = false
}) => {
  const tipEnabledState = useAppSelector(selectTipEnabled);
  const paymentModes = useAppSelector(selectInvoicePaymentModes);
  const invoiceTotal = useAppSelector(selectInvoiceFinalAmount);
  const noPaymentMethodsAvailable = useAppSelector(selectNoPaymentMethodsAvailable);
  const availablePaymentModes = useAppSelector(selectAvailablePaymentModes);
  const dispatch = useAppDispatch();
  const { selectedInvoiceTab, setSelectedInvoiceTab } = useContext<any>(context);

  const isTabSelected = selectedInvoiceTab === InvoiceTabEnum.PAYMENT_OPTIONS;

  useEffect(() => {
    // api call is unnecessary during edit invoice
    if (taxesAndFees && !isInvoiceEdit) {
      dispatch(updateTipEnabled(taxesAndFees?.tip?.enabled));
    }
  }, [taxesAndFees]);

  const onTipConfigChange = () => {
    setSelectedInvoiceTab(InvoiceTabEnum.PAYMENT_OPTIONS);
    dispatch(toggleTipEnabled());
  };

  const onTipsLearnMoreClick = () => {
    openScanpayFAQInNewTab(CleverTapEventsInvoices.webInvoicesCreateInvoiceTipsFaqClick);
  };

  const handleUpdatePaymentModes = (updatedPaymentModes: InvoiceLevelPaymentModes[]) => {
    dispatch(updateInvoicePaymentModes(updatedPaymentModes));
  };

  return (
    <div>
      <div className={`mb-2 text-xl font-bold ${isTabSelected ? 'text-primaryBtn' : 'text-heading'}`}>
        Payment options
      </div>
      <div
        className={`flex flex-col gap-[30px] rounded-md border px-[30px] py-5 1439px:p-10 ${
          isTabSelected ? 'border-primaryBtn' : 'border-borderGray'
        }`}>
        <div>
          <InvoicePaymentMethodsSection
            paymentModes={paymentModes}
            onUpdateInvoicePaymentModes={handleUpdatePaymentModes}
            invoiceTotal={invoiceTotal}
            noPaymentMethods={noPaymentMethodsAvailable}
            availablePaymentModes={availablePaymentModes}
          />
        </div>

        <div>
          <div className="mb-3 text-17px font-semibold text-heading">Tips</div>
          <div className="flex flex-row items-center gap-3 rounded border border-x-borderGray p-5">
            <CustomSwitch
              enabled={tipEnabledState}
              toggleSwitch={onTipConfigChange}
              id="create-invoice-allow-tip-toggle-switch"
            />
            <div className="text-17px font-semibold text-primaryText">Allow customers to leave a tip</div>
            <div onClick={onTipsLearnMoreClick} className="ml-auto flex cursor-pointer flex-row items-center">
              <div id="create-invoice-learn-more" className="mr-2 text-sbase font-semibold text-secondary">
                Learn more
              </div>
              <ArrowRightIcon className="shrink-0" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentOptionsSection;
